import { Injectable } from '@angular/core';
import { ApiService } from '../../../shared/_services/api.service';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import {
  AudienceTargetingSegment,
  CloneExchangeDeal,
  CommonDataModel,
  DealPortfolio,
  Dv360InstantDealLog,
  ExchangeDeal,
  ThinExchangeDeal,
  OptionsList, PadAgreement,
  Personnel,
  ResponseFromServer,
  SupplyPublisherMedia,
  ThinPublisher
} from '../../../shared/_models/models';
import { DspBuyersQueryParams, DspBuyersResponse } from '@/app/shared/_models/dsp-buyer.model';
import { UniversalMediaList } from '../../universal-media-lists/models';
import { UniversalMediaListsApiService } from '../../universal-media-lists/universal-media-lists.api-service';
import { ExchangeDealFormApiService } from '../exchange-deal-form/_services/exchange-deal-form.api-service';
import { AuthenticationService } from '../../../auth/_services/authentication.service';

@Injectable({providedIn: 'root'})

export class ExchangeDealsService {
  private userPortfolioScope$?: Observable<number[]>;
  private userPortfolioScopeUserId?: number;

  constructor(
    private readonly api: ApiService,
    private readonly umlApi: UniversalMediaListsApiService,
    private readonly exchangeDealFormApiService: ExchangeDealFormApiService,
    private readonly auth: AuthenticationService
  ) {}

  getPortfolios(dealPartnerIds: number[] = [], ownedOnly = false): Observable<DealPortfolio[]> {
    return this.exchangeDealFormApiService.getPortfolios(dealPartnerIds, ownedOnly);
  }

  /** Cached per logged-in user, so a login without a page reload never reuses the previous user's pins. */
  getUserPortfolioScope(): Observable<number[]> {
    const userId = this.auth.currentUserValue?.id;
    if (!this.userPortfolioScope$ || this.userPortfolioScopeUserId !== userId) {
      this.userPortfolioScopeUserId = userId;
      this.userPortfolioScope$ = this.exchangeDealFormApiService.getUserPortfolioScope().pipe(
        catchError(() => of([])),
        shareReplay(1)
      );
    }
    return this.userPortfolioScope$;
  }

  createDeal(currentDeal: ExchangeDeal): Observable<ResponseFromServer<ExchangeDeal>> {
    return this.exchangeDealFormApiService.createExchangeDeal(currentDeal);
  }

  updateDeal(dealId: number, currentDeal: ExchangeDeal): Observable<ResponseFromServer<ExchangeDeal>> {
    return this.api.updateExchangeDeal(dealId, currentDeal);
  }

  cloneDeal(currentDeal: CloneExchangeDeal, dealId: number): Observable<ResponseFromServer<ExchangeDeal>> {
    return this.api.cloneExchangeDeal(currentDeal, dealId);
  }

  getCurrentDeal(dealId: number): Observable<ResponseFromServer<ExchangeDeal>> {
    return this.api.getCurrentExchangeDeal(dealId);
  }

  getCurrentPartnerForecastDeal(partnerForecastDealId: number): Observable<ResponseFromServer<ExchangeDeal>> {
    return this.api.getCurrentPartnerForecastDeal(partnerForecastDealId);
  }

  getCurrentDv360InstantDealLog(dealId: number): Observable<ResponseFromServer<Dv360InstantDealLog>> {
    return this.api.getCurrentDv360InstantDealLog(dealId);
  }

  getDeviceTypes(): Observable<OptionsList> {
    return this.api.getDeviceTypes() as Observable<OptionsList>;
  }

  fetchExternalDealId(): Observable<ResponseFromServer<string>> {
    return this.api.getExternalDealId();
  }

  getAudienceTargeting(filters: string = ''): Observable<CommonDataModel<AudienceTargetingSegment[]>> {
    return this.api.getRxSegments(filters);
  }

  getUniversalMediaLists(filters: string = ''): Observable<CommonDataModel<UniversalMediaList[]>> {
    return this.umlApi.getUniversalMediaLists(filters);
  }

  getExchangeDeals(filters: string = ''): Observable<CommonDataModel<ExchangeDeal[]>> {
    return this.api.getExchangeDeals(filters);
  }

  getThinExchangeDeals(filters: string = ''): Observable<CommonDataModel<ThinExchangeDeal[]>> {
    return this.api.getThinExchangeDeals(filters);
  }

  getExchangePersonnel(): Observable<ResponseFromServer<Personnel[]>> {
    return this.api.getExchangePersonnel();
  }

  getPublishers(query: any): Observable<ResponseFromServer<ThinPublisher[]>> {
    return this.api.getPublishers(query);
  }

  getExchangeDealPads(filters: string = ''): Observable<CommonDataModel<PadAgreement[]>> {
    return this.api.getExchangePadAgreements(filters);
  }

  getPublisherNamesByIds(publisherIds: string[]): Observable<ResponseFromServer<SupplyPublisherMedia[]>> {
    return this.api.getPublisherNamesByIds(publisherIds);
  }

  getDspBuyers(dspIdentifier: string, params?: DspBuyersQueryParams): Observable<ResponseFromServer<DspBuyersResponse>> {
    return this.api.getDspBuyers(dspIdentifier, params);
  }
}
